import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { calculateItemAmounts } from "./helper";

const SS_TEMPLATE_HEADERS = [
  "Line Item ID",
  "Item ID",
  "Mapping ID",
  "Subcategory",
  "Item",
  "Description",
  "HSN",
  "UOM",
  "Qty",
  "Rate",
  "Tax %",
  "Amount",
  "Tax Amt",
  "Total",
  "Budget Head",
  "Remarks",
];
const HIDDEN_COLUMN_COUNT = 3;
const COL = Object.fromEntries(
  SS_TEMPLATE_HEADERS.map((h, i) => [h, XLSX.utils.encode_col(i)])
);

const QTY_CELL_FORMAT = "0.00";
const REMARKS_CELL_FORMAT = "@";
const UNLOCKED_NUM_FMT_IDS = ["2", "49"];

// Speed bump only (the password lives in client code) -- import never reads any of the
// locked columns anyway, so an unprotected edit can't change what gets imported.
const TEMPLATE_SHEET_PASSWORD = "subway-ss-pr";

export const getSSRowKey = (row) =>
  row.item != null ? `${row.item}|${row.categoryMappingId ?? ""}` : row._tempId;

const unlockEditableCellStyles = (xlsxBinary) => {
  const cfb = XLSX.CFB.read(xlsxBinary, { type: "binary" });
  const stylesFile = XLSX.CFB.find(cfb, "/xl/styles.xml");
  if (!stylesFile) throw new Error("styles.xml not found in generated template");
  const xml = new TextDecoder().decode(Uint8Array.from(stylesFile.content));
  const idPattern = UNLOCKED_NUM_FMT_IDS.join("|");
  const patched = xml.replace(
    new RegExp(`<xf numFmtId="(${idPattern})"([^>]*?)/>`, "g"),
    '<xf numFmtId="$1"$2 applyProtection="1"><protection locked="0"/></xf>'
  );
  const bytes = new TextEncoder().encode(patched);
  stylesFile.content = bytes;
  stylesFile.size = bytes.length;
  return XLSX.CFB.write(cfb, { fileType: "zip", type: "array" });
};

export const downloadSingleStorePRTemplate = ({
  selectedLineItems = [],
  prItemsByLineItem = {},
  subcategoryMap = {},
  siteCode = "",
}) => {
  const ws = {};
  SS_TEMPLATE_HEADERS.forEach((h, c) => {
    ws[XLSX.utils.encode_cell({ r: 0, c })] = { t: "s", v: h };
  });

  let r = 0;
  selectedLineItems.forEach((lineItem) => {
    const subcategories = subcategoryMap[lineItem.id] || [];
    (prItemsByLineItem[lineItem.id] || []).forEach((row) => {
      if (row.item == null) return;
      r += 1;
      const n = r + 1; // 1-based Excel row number
      const subCategoryName =
        subcategories.find((sc) => String(sc._id || sc.id) === String(row.subCategory))?.name || "";
      const values = {
        "Line Item ID": { t: "s", v: String(lineItem.id) },
        "Item ID": { t: "s", v: String(row.item) },
        "Mapping ID": { t: "s", v: row.categoryMappingId != null ? String(row.categoryMappingId) : "" },
        Subcategory: { t: "s", v: subCategoryName },
        Item: { t: "s", v: row.itemName || "" },
        Description: { t: "s", v: row.description || "" },
        HSN: { t: "s", v: row.hsnCode || "" },
        UOM: { t: "s", v: row.uom || "" },
        // Blank (empty string, not 0) -- a real stub cell would be dropped by the writer and
        // lose its unlocked style.
        Qty: { t: "s", v: "", z: QTY_CELL_FORMAT },
        Rate: { t: "n", v: parseFloat(row.estimatedRate) || 0 },
        "Tax %": { t: "n", v: parseFloat(row.taxRate) || 0 },
        // Reference-only live preview; never read back on import.
        Amount: { t: "n", v: 0, f: `ROUND(N(${COL.Qty}${n})*${COL.Rate}${n},2)` },
        "Tax Amt": { t: "n", v: 0, f: `ROUND(${COL.Amount}${n}*${COL["Tax %"]}${n}/100,2)` },
        Total: { t: "n", v: 0, f: `${COL.Amount}${n}+${COL["Tax Amt"]}${n}` },
        "Budget Head": { t: "s", v: lineItem.name || row.budgetHead || "" },
        Remarks: { t: "s", v: "", z: REMARKS_CELL_FORMAT },
      };
      SS_TEMPLATE_HEADERS.forEach((h, c) => {
        ws[XLSX.utils.encode_cell({ r, c })] = values[h];
      });
    });
  });

  if (r === 0) return 0;

  ws["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r, c: SS_TEMPLATE_HEADERS.length - 1 },
  });
  ws["!cols"] = SS_TEMPLATE_HEADERS.map((h, i) =>
    i < HIDDEN_COLUMN_COUNT
      ? { hidden: true, wch: 10 }
      : { wch: ["Item", "Description", "Subcategory", "Budget Head", "Remarks"].includes(h) ? 30 : 12 }
  );
  // Everything protected except resizing columns; locked + unlocked cells stay selectable.
  ws["!protect"] = { password: TEMPLATE_SHEET_PASSWORD, formatColumns: false };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "data");
  const binary = XLSX.write(wb, { bookType: "xlsx", type: "binary" });
  const out = unlockEditableCellStyles(binary);
  saveAs(
    new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    }),
    `single-store-pr-items${siteCode ? `-${siteCode}` : ""}.xlsx`
  );
  return r;
};

// Maps excel/pr-item-details?prScope=SINGLE_STORE's { items, errors } onto the rows
// currently in the grid. Same division of labour as the HO mapper: the endpoint only reads
// identity/Qty/Remarks, and everything else (rate, tax, amounts) comes from the live grid
// row -- which was itself resolved from the current catalog + approved vendor mappings at
// page load -- so stale template values are never used. A file row whose item is no longer
// in the grid (deactivated/recategorized since download) is reported, not imported.
// Returns { updatesByLineItem: { [lineItemId]: { [rowKey]: { quantity, remarks } } }, errors, includedCount }.
export const mapSingleStorePrUploadResponse = (
  uploadedData,
  { selectedLineItems = [], prItemsByLineItem = {} } = {}
) => {
  const rawItems = Array.isArray(uploadedData?.items) ? uploadedData.items : [];
  const errors = Array.isArray(uploadedData?.errors) ? [...uploadedData.errors] : [];
  const updatesByLineItem = {};

  const lineItemsById = new Map(selectedLineItems.map((li) => [String(li.id), li]));
  const gridKeysByLineItem = new Map(
    selectedLineItems.map((li) => [
      String(li.id),
      new Set((prItemsByLineItem[li.id] || []).map(getSSRowKey)),
    ])
  );

  rawItems.forEach((row) => {
    const label = row.itemName || row.itemId;
    const lineItem = lineItemsById.get(String(row.lineItemId));
    if (!lineItem) {
      errors.push({
        row: row.row,
        message: `Item "${label}" belongs to a budget head that is not part of this PR`,
      });
      return;
    }
    const rowKey = `${row.itemId}|${row.mappingId ?? ""}`;
    if (!gridKeysByLineItem.get(String(lineItem.id)).has(rowKey)) {
      errors.push({
        row: row.row,
        message: `Item "${label}" is no longer available in the current catalog for "${lineItem.name}"`,
      });
      return;
    }
    const quantity = parseFloat(row.quantity) || 0;
    const updates = (updatesByLineItem[lineItem.id] = updatesByLineItem[lineItem.id] || {});
    // Same item listed twice adds up, like the HO mapper's Self-destination merge.
    const prev = updates[rowKey];
    updates[rowKey] = {
      quantity: (prev?.quantity || 0) + quantity,
      remarks: row.remarks || prev?.remarks || "",
    };
  });

  const includedCount = Object.values(updatesByLineItem)
    .flatMap((u) => Object.values(u))
    .filter((u) => u.quantity > 0).length;

  return { updatesByLineItem, errors, includedCount };
};

// Applies one line item's uploaded quantities onto its grid rows. Rows present in the file
// take the file's Qty (blank/0 -> 0, i.e. excluded) and its Remarks when non-blank; rows
// absent from the file are left as they were. Amounts are always recomputed from the
// grid's own rate/tax.
export const applySingleStoreUploadToRows = (rows, updates = {}) =>
  rows.map((row) => {
    const update = updates[getSSRowKey(row)];
    if (!update) return row;
    const next = {
      ...row,
      quantity: update.quantity,
      remarks: update.quantity > 0 && update.remarks ? update.remarks : row.remarks,
      // Same reasoning as SingleStorePRRow.handleQtyChange: a flat single-store quantity is
      // authoritative, so drop any stale allocations.
      allocations: [],
    };
    return { ...next, ...calculateItemAmounts(next) };
  });
